/**
 * Agentic Intent API Service
 * Handles communication with the /api/v1/agentic endpoints
 */
import axios from 'axios';
import type {
  AgenticBundle,
  ExecuteRequest,
  ExecutionResult,
  IntentRequest,
  IntentSuggestion,
} from '../types/agentic';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

export interface CollaborationAgentStatus {
  agentId?: string;
  agentType?: string;
  name?: string;
  status: string;
  currentTask?: string;
  lastHeartbeat?: string;
  lastActivity?: string;
  metrics?: Record<string, unknown>;
}

export interface CollaborationStatus {
  agents: CollaborationAgentStatus[];
  activeSessions: number;
  activeExecutions: number;
  activeOpportunities: number;
  metrics: Record<string, unknown>;
  timestamp: string;
}

// ==================== Liquidity Types ====================
export interface LiquidityScan {
  cusip: string;
  side: string;
  scanTime: string;
  overallScore: number;
  sources: string[];
  recommendation: string;
  trace: TraceLiquidity;
  axes: AxeLiquidity;
  clob: ClobLiquidity;
  aggregateMetrics: Record<string, any>;
}

export interface TraceLiquidity {
  hasData: boolean;
  error?: string;
  liquidityScore: number;
  tradeCount: number;
  totalVolume: number;
  avgTradeSize: number;
  avgSpread: number;
  mostActiveDealer: string;
  windowHours: number;
  recentTrades: RecentTrade[];
  probableDealers: ProbableDealer[];
}

export interface RecentTrade {
  side: string;
  quantity: number;
  price: number;
  yield: number;
  spread: number;
  dealer: string;
  timestamp: string;
  isInterdealer: boolean;
}

export interface ProbableDealer {
  dealerCode: string;
  dealerName: string;
  probability: number;
  tradeCount: number;
  volume: number;
  reasoning: string;
}

export interface AxeLiquidity {
  hasData: boolean;
  error?: string;
  axeCount: number;
  totalAvailableSize: number;
  bestPrice: number;
  bestSize: number;
  bestDealer: string;
  axes: ActiveAxe[];
}

export interface ActiveAxe {
  dealerCode: string;
  dealerName: string;
  side: string;
  price: number;
  yield: number;
  quantity: number;
  axeLevel: string;
  source: string;
  expiresAt: string;
}

export interface ClobLiquidity {
  hasData: boolean;
  error?: string;
  hasBBO: boolean;
  bidPrice: number;
  bidSize: number;
  bidVenue: string;
  askPrice: number;
  askSize: number;
  askVenue: string;
  spread: number;
  midPrice: number;
  totalBidDepth: number;
  totalAskDepth: number;
  restingOrders: RestingOrder[];
}

export interface RestingOrder {
  orderId: string;
  side: string;
  price: number;
  quantity: number;
  venue: string;
  expiresAt: string;
  orderType: string;
}

export interface QuickLiquidityCheck {
  cusip: string;
  checkTime: string;
  liquidityScore: number;
  classification: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  message: string;
  tradeCount: number;
  totalVolume: number;
  mostActiveDealer: string;
}

export interface DealerRecommendation {
  dealerCode: string;
  dealerName: string;
  probability: number;
  reasoning: string;
  recentVolume: number;
  lastTradeTime: string;
  sources: string[];
  hasActiveAxe: boolean;
  axePrice?: number;
  axeSize?: number;
  axeLevel?: string;
}

export interface PortfolioLiquidityScan {
  scanTime: string;
  totalSecurities: number;
  liquidCount: number;
  illiquidCount: number;
  avgLiquidityScore: number;
  liquidSecurities: string[];
  illiquidSecurities: string[];
  securityScans: QuickLiquidityCheck[];
}

/**
 * Liquidity API for real-time market liquidity data
 */
export const liquidityApi = {
  async scanLiquidity(cusip: string, side: string = 'BUY'): Promise<LiquidityScan> {
    try {
      const response = await axios.get<LiquidityScan>(`/api/liquidity/scan/${cusip}`, {
        params: { side },
      });
      return response.data;
    } catch (error) {
      return {
        cusip,
        side,
        scanTime: new Date().toISOString(),
        overallScore: 0,
        sources: [],
        recommendation: getApiErrorMessage(error, 'Liquidity data is unavailable.'),
        trace: {
          hasData: false,
          error: 'Liquidity data unavailable',
          liquidityScore: 0,
          tradeCount: 0,
          totalVolume: 0,
          avgTradeSize: 0,
          avgSpread: 0,
          mostActiveDealer: '',
          windowHours: 4,
          recentTrades: [],
          probableDealers: [],
        },
        axes: {
          hasData: false,
          error: 'Active axe data unavailable',
          axeCount: 0,
          totalAvailableSize: 0,
          bestPrice: 0,
          bestSize: 0,
          bestDealer: '',
          axes: [],
        },
        clob: {
          hasData: false,
          error: 'Venue order book data unavailable',
          hasBBO: false,
          bidPrice: 0,
          bidSize: 0,
          bidVenue: '',
          askPrice: 0,
          askSize: 0,
          askVenue: '',
          spread: 0,
          midPrice: 0,
          totalBidDepth: 0,
          totalAskDepth: 0,
          restingOrders: [],
        },
        aggregateMetrics: {},
      };
    }
  },

  async quickCheck(cusip: string): Promise<QuickLiquidityCheck> {
    try {
      const response = await axios.get<QuickLiquidityCheck>(`/api/liquidity/quick-check/${cusip}`);
      return response.data;
    } catch (error) {
      return {
        cusip,
        checkTime: new Date().toISOString(),
        liquidityScore: 0,
        classification: 'NONE',
        message: getApiErrorMessage(error, 'Liquidity data unavailable'),
        tradeCount: 0,
        totalVolume: 0,
        mostActiveDealer: '',
      };
    }
  },

  async findBestDealers(cusip: string, side: string, quantity: number): Promise<DealerRecommendation[]> {
    try {
      const response = await axios.get<DealerRecommendation[]>(`/api/liquidity/dealers/${cusip}`, {
        params: { side, quantity },
      });
      return response.data;
    } catch {
      return [];
    }
  },

  async scanPortfolio(cusips: string[]): Promise<PortfolioLiquidityScan> {
    try {
      const response = await axios.post<PortfolioLiquidityScan>('/api/liquidity/portfolio-scan', cusips);
      return response.data;
    } catch {
      return {
        scanTime: new Date().toISOString(),
        totalSecurities: cusips.length,
        liquidCount: 0,
        illiquidCount: cusips.length,
        avgLiquidityScore: 0,
        liquidSecurities: [],
        illiquidSecurities: cusips,
        securityScans: [],
      };
    }
  },
};

/**
 * Collaboration API for multi-agent coordination
 */
export const collaborationApi = {
  async discover(): Promise<{ message: string; evaluationsStarted: number; timestamp: string }> {
    const response = await axios.post('/api/collaboration/discover');
    return response.data;
  },

  async getStatus(): Promise<CollaborationStatus | null> {
    try {
      const response = await axios.get<CollaborationStatus>('/api/collaboration/status');
      return response.data;
    } catch {
      return null;
    }
  },

  async runCompliance(): Promise<{ message: string; status: string }> {
    try {
      const response = await axios.post('/api/collaboration/compliance');
      return response.data;
    } catch (error) {
      return {
        message: getApiErrorMessage(error, 'Collaboration compliance endpoint is unavailable.'),
        status: 'unavailable',
      };
    }
  },

  async scanLiquidity(): Promise<{ message: string; status: string }> {
    try {
      const response = await axios.post('/api/collaboration/liquidity');
      return response.data;
    } catch (error) {
      return {
        message: getApiErrorMessage(error, 'Collaboration liquidity endpoint is unavailable.'),
        status: 'unavailable',
      };
    }
  },

  async stopDiscovery(): Promise<{ message: string }> {
    try {
      const response = await axios.post('/api/collaboration/stop');
      return response.data;
    } catch (error) {
      return { message: getApiErrorMessage(error, 'Discovery stop endpoint is unavailable.') };
    }
  },
};

export const agenticApi = {
  async processIntent(request: IntentRequest): Promise<AgenticBundle> {
    try {
      const response = await axios.post<AgenticBundle>('/api/v1/agentic/intent', request);
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to process portfolio intent.'));
    }
  },

  async executeBundle(bundleId: string, options: ExecuteRequest = {}): Promise<ExecutionResult> {
    try {
      const response = await axios.post<ExecutionResult>(`/api/v1/agentic/execute/${bundleId}`, options);
      return response.data;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to execute the approved bundle.'));
    }
  },

  async getSuggestions(): Promise<IntentSuggestion[]> {
    try {
      const response = await axios.get<string[]>('/api/v1/agentic/suggestions');
      return response.data.map(text => ({ text, category: 'Suggested' }));
    } catch {
      return [];
    }
  },

  async runDemoScenario(scenario: 'rate-shock' | 'complex-rebalance'): Promise<AgenticBundle> {
    try {
      const response = await axios.post<{ bundle?: AgenticBundle; data?: AgenticBundle }>(`/api/v1/agentic/demo/${scenario}`);
      const bundle = response.data?.bundle || response.data?.data;

      if (!bundle) {
        throw new Error('Scenario response did not include a bundle.');
      }

      return bundle;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to run demo scenario.'));
    }
  },
};

export default agenticApi;
